/**
 * Pattern of Life — Intelligence Summary dashboard.
 *
 * The primary, executive-first view of a pol_report job. Transforms the raw
 * report payload into an intelligence product: key findings, behavioral
 * insight cards, an activity timeline with anomaly callouts, supporting
 * geospatial evidence (reduced map), rhythm/frequency analytics, and an
 * AI-generated assessment with recommended follow-on analysis.
 *
 * Everything here is derived client-side from job.results_summary.pol_report;
 * sections degrade gracefully when an analysis type was excluded from the run.
 */

import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  WarningAmberOutlined as AnomalyIcon,
  PlaceOutlined as PlaceIcon,
  EventRepeat as PatternIcon,
  VerifiedOutlined as ConfidenceIcon,
  AutoAwesome as AiIcon,
  MapOutlined as MapIcon,
  QueryStats as AnalyticsIcon,
  FlagOutlined as FindingIcon,
  ArrowForward as ArrowIcon,
  PersonSearch as EntityIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Cell,
} from 'recharts';

import { DwellLocation, Route as RouteType } from '@signals/app-shell/types';
import EnhancedMapVisualizationGL from './EnhancedMapVisualizationGL';
import {
  PolEntity,
  PolReportPayload,
  ANOMALY_TYPE_LABELS,
  SEVERITY_RANK,
  DAY_LABELS,
  formatDateTime,
  formatDurationSeconds,
  formatHour,
  normalizeDayIndex,
} from './polReportShared';

interface PolReportDashboardProps {
  report: PolReportPayload;
  mapDwells: DwellLocation[];
  mapRoutes: RouteType[];
}

// ---------------------------------------------------------------------------
// Derived-data types
// ---------------------------------------------------------------------------

type FindingTone = 'critical' | 'warning' | 'info' | 'positive';

interface KeyFinding {
  tone: FindingTone;
  title: string;
  detail: string;
}

interface TimelineDay {
  day: string; // yyyy-mm-dd
  label: string;
  avgAtypicality: number;
  maxAtypicality: number;
  anomalyCount: number;
  highSeverityCount: number;
}

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

const SectionHeader: React.FC<{
  icon: React.ElementType;
  overline: string;
  title: string;
  caption?: string;
}> = ({ icon: Icon, overline, title, caption }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
        color: 'primary.main',
        flexShrink: 0,
        mt: 0.25,
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
    </Box>
    <Box>
      <Typography
        variant="overline"
        sx={{ letterSpacing: 1.2, lineHeight: 1.4, color: 'primary.main', display: 'block' }}
      >
        {overline}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Box>
  </Stack>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent?: string;
}> = ({ label, value, sub, accent }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderTop: '3px solid',
      borderTopColor: accent || 'primary.main',
    }}
  >
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2, my: 0.25 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const toneStyles = (
  theme: any
): Record<FindingTone, { color: string; bg: string; label: string }> => ({
  critical: {
    color: theme.palette.error.main,
    bg: alpha(theme.palette.error.main, 0.08),
    label: 'Critical',
  },
  warning: {
    color: theme.palette.warning.main,
    bg: alpha(theme.palette.warning.main, 0.08),
    label: 'Notable',
  },
  info: {
    color: theme.palette.info.main,
    bg: alpha(theme.palette.info.main, 0.08),
    label: 'Insight',
  },
  positive: {
    color: theme.palette.success.main,
    bg: alpha(theme.palette.success.main, 0.08),
    label: 'Baseline',
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PolReportDashboard: React.FC<PolReportDashboardProps> = ({
  report,
  mapDwells,
  mapRoutes,
}) => {
  const theme = useTheme();
  const tones = toneStyles(theme);
  const [mapOpen, setMapOpen] = useState(false);

  const sections = report.sections || {};
  const anomalies: any[] = sections.anomalies || [];
  const atypicality: any[] = sections.atypicality || [];
  const dwells: any[] = sections.dwells || [];
  const routes: any[] = sections.repeated_routes || [];
  const patterns: any[] = sections.patterns || [];
  const correlations: any[] = sections.correlations || [];
  const digital: any[] = sections.digital || [];

  // ----- KPI strip ---------------------------------------------------------

  const windowDays = useMemo(() => {
    const start = new Date(report.date_range.start).getTime();
    const end = new Date(report.date_range.end).getTime();
    return Math.max(1, Math.round((end - start) / 86400000));
  }, [report.date_range]);

  const anomalyStats = useMemo(() => {
    const total =
      report.section_counts?.anomalies ??
      (anomalies.length ||
        report.entities.reduce((acc, e) => acc + (e.anomaly_count || 0), 0));
    const high = anomalies.filter(
      (a) => a.severity === 'high' || a.severity === 'critical'
    ).length;
    const unacknowledged = anomalies.filter(
      (a) => (a.severity === 'high' || a.severity === 'critical') && !a.is_acknowledged
    ).length;
    return { total, high, unacknowledged };
  }, [anomalies, report]);

  const confidence = useMemo(() => {
    const withConf = report.entities.filter((e) => e.data_confidence != null);
    const avg =
      withConf.length > 0
        ? withConf.reduce((acc, e) => acc + (e.data_confidence || 0), 0) / withConf.length
        : null;
    const baselines = report.entities.filter((e) => e.baseline_established).length;
    const avgDays =
      report.entities.length > 0
        ? Math.round(
            report.entities.reduce((acc, e) => acc + (e.days_with_data || 0), 0) /
              report.entities.length
          )
        : 0;
    return { avg, baselines, avgDays };
  }, [report.entities]);

  const uniqueLocations = useMemo(() => {
    if (dwells.length > 0) {
      return new Set(dwells.map((d) => d.location_name || `${d.lat},${d.lon}`)).size;
    }
    return report.entities.reduce((acc, e) => acc + (e.unique_locations || 0), 0);
  }, [dwells, report.entities]);

  // ----- Key findings (rule-generated) -------------------------------------

  const keyFindings: KeyFinding[] = useMemo(() => {
    const findings: KeyFinding[] = [];

    if (anomalyStats.high > 0) {
      const worst = [...anomalies].sort(
        (a, b) =>
          (SEVERITY_RANK[b.severity] || 0) - (SEVERITY_RANK[a.severity] || 0) ||
          (b.deviation_score || 0) - (a.deviation_score || 0)
      )[0];
      findings.push({
        tone: 'critical',
        title: `${anomalyStats.high} high-severity anomal${anomalyStats.high === 1 ? 'y' : 'ies'} detected`,
        detail: worst
          ? `Most significant: ${ANOMALY_TYPE_LABELS[worst.anomaly_type] || worst.anomaly_type} by ${worst.entity_id} on ${formatDateTime(worst.anomaly_time)}.`
          : 'Review the anomaly section for details.',
      });
    }

    const topAtypical = [...atypicality].sort(
      (a, b) => (b.atypicality_score || 0) - (a.atypicality_score || 0)
    )[0];
    if (topAtypical && (topAtypical.atypicality_score || 0) >= 0.5) {
      findings.push({
        tone: 'warning',
        title: `${topAtypical.entity_id} had a highly atypical day`,
        detail: `${new Date(topAtypical.day).toLocaleDateString()} scored ${Math.round(
          (topAtypical.atypicality_score || 0) * 100
        )}% atypical with ${topAtypical.total_anomaly_count || 0} detector events.`,
      });
    }

    const breaking = patterns.filter(
      (p) => String(p.status || '').toLowerCase() === 'breaking'
    );
    if (breaking.length > 0) {
      const names = Array.from(new Set(breaking.map((p) => p.entity_id))).slice(0, 3);
      findings.push({
        tone: 'warning',
        title: `${breaking.length} established routine${breaking.length === 1 ? ' is' : 's are'} breaking`,
        detail: `Entities departing from previously stable patterns: ${names.join(', ')}${breaking.length > names.length ? '…' : ''}.`,
      });
    }

    const topCorrelation = correlations[0];
    if (topCorrelation && (topCorrelation.correlation_strength || 0) >= 0.5) {
      findings.push({
        tone: 'info',
        title: `Strong association: ${topCorrelation.entity_a_id} ↔ ${topCorrelation.entity_b_id}`,
        detail: `${Math.round((topCorrelation.correlation_strength || 0) * 100)}% correlation across ${topCorrelation.occurrence_count || 0} co-occurrences${(topCorrelation.shared_locations || []).length ? ` and ${(topCorrelation.shared_locations || []).length} shared locations` : ''}.`,
      });
    }

    const sharedRoute = routes.find((r) => (r.shared_with_entities || []).length > 0);
    if (sharedRoute) {
      findings.push({
        tone: 'info',
        title: 'Shared movement corridor identified',
        detail: `${sharedRoute.entity_id}'s ${sharedRoute.route_name || 'route'} is shared with ${(sharedRoute.shared_with_entities || []).length} other entit${(sharedRoute.shared_with_entities || []).length === 1 ? 'y' : 'ies'} (${sharedRoute.usage_count || 0} trips).`,
      });
    }

    const topRoute = routes[0];
    if (topRoute && findings.length < 5) {
      findings.push({
        tone: 'positive',
        title: `Dominant route: ${topRoute.route_name || 'unnamed route'}`,
        detail: `${topRoute.entity_id} made ${topRoute.usage_count || 0} trips at ${Math.round((topRoute.consistency_score || 0) * 100)}% consistency${topRoute.peak_day ? `, peaking ${topRoute.peak_day}${topRoute.peak_hour != null ? ` ~${formatHour(topRoute.peak_hour)}` : ''}` : ''}.`,
      });
    }

    const topDwell = dwells[0];
    if (topDwell && findings.length < 5) {
      findings.push({
        tone: 'positive',
        title: `Anchor location: ${topDwell.location_name || 'unnamed location'}`,
        detail: `${topDwell.visit_count || 0} visits totaling ${formatDurationSeconds(topDwell.total_duration_seconds)} by ${topDwell.entity_id}.`,
      });
    }

    if (findings.length === 0) {
      findings.push({
        tone: 'positive',
        title: 'No significant deviations detected',
        detail:
          'Observed behavior across the reporting window is consistent with established baselines.',
      });
    }
    return findings.slice(0, 5);
  }, [anomalies, anomalyStats, atypicality, patterns, correlations, routes, dwells]);

  // ----- Activity timeline --------------------------------------------------

  const timeline: TimelineDay[] = useMemo(() => {
    const byDay = new Map<string, TimelineDay>();
    const ensure = (iso: string): TimelineDay => {
      const key = iso.slice(0, 10);
      let d = byDay.get(key);
      if (!d) {
        d = {
          day: key,
          label: new Date(`${key}T00:00:00`).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          }),
          avgAtypicality: 0,
          maxAtypicality: 0,
          anomalyCount: 0,
          highSeverityCount: 0,
        };
        byDay.set(key, d);
      }
      return d;
    };

    const scores = new Map<string, number[]>();
    atypicality.forEach((m) => {
      if (!m.day) return;
      const d = ensure(m.day);
      const key = d.day;
      const list = scores.get(key) || [];
      list.push(m.atypicality_score || 0);
      scores.set(key, list);
      d.maxAtypicality = Math.max(d.maxAtypicality, m.atypicality_score || 0);
    });
    scores.forEach((list, key) => {
      const d = byDay.get(key);
      if (d) d.avgAtypicality = list.reduce((a, b) => a + b, 0) / list.length;
    });

    anomalies.forEach((a) => {
      if (!a.anomaly_time) return;
      const d = ensure(a.anomaly_time);
      d.anomalyCount += 1;
      if (a.severity === 'high' || a.severity === 'critical') d.highSeverityCount += 1;
    });

    return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [atypicality, anomalies]);

  const notableEvents = useMemo(
    () =>
      [...anomalies]
        .sort(
          (a, b) =>
            (SEVERITY_RANK[b.severity] || 0) - (SEVERITY_RANK[a.severity] || 0) ||
            (b.deviation_score || 0) - (a.deviation_score || 0)
        )
        .slice(0, 6),
    [anomalies]
  );

  // ----- Analytics: rhythm heatmap + day-of-week + location ranking ---------

  const rhythm = useMemo(() => {
    const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    let hasData = false;

    const add = (dayIdx: number | null, hour: number | null | undefined, weight: number) => {
      if (hour == null || hour < 0 || hour > 23 || weight <= 0) return;
      hasData = true;
      if (dayIdx == null) {
        // Unknown day: spread across the week so the hour column still shows.
        for (let d = 0; d < 7; d++) grid[d][hour] += weight / 7;
      } else {
        grid[dayIdx][hour] += weight;
      }
    };

    routes.forEach((r) => {
      const weight = Math.max(1, r.usage_count || 1);
      const hours: number[] = (r.typical_hours || [])
        .map((h: any) => (typeof h === 'number' ? h : parseInt(h, 10)))
        .filter((h: number) => Number.isFinite(h));
      const days = (r.typical_days || [])
        .map(normalizeDayIndex)
        .filter((d: number | null): d is number => d != null);
      const effHours = hours.length ? hours : r.peak_hour != null ? [r.peak_hour] : [];
      const effDays = days.length ? days : [normalizeDayIndex(r.peak_day)];
      effHours.forEach((h: number) =>
        effDays.forEach((d: number | null) => add(d, h, weight / Math.max(1, effHours.length)))
      );
    });

    patterns.forEach((p) => {
      const weight = Math.max(1, p.occurrence_count || 1);
      const startH = parseInt(String(p.time_window_start || '').split(':')[0], 10);
      const endH = parseInt(String(p.time_window_end || '').split(':')[0], 10);
      const days = (p.days_of_week || [])
        .map(normalizeDayIndex)
        .filter((d: number | null): d is number => d != null);
      if (Number.isFinite(startH)) {
        const span = Number.isFinite(endH) && endH >= startH ? endH - startH + 1 : 1;
        for (let h = startH; h < startH + span && h < 24; h++) {
          if (days.length) days.forEach((d: number) => add(d, h, weight / span));
          else add(null, h, weight / span);
        }
      }
    });

    digital.forEach((p) => {
      add(normalizeDayIndex(p.peak_day), p.peak_hour, Math.max(1, p.occurrence_count || 1));
    });

    anomalies.forEach((a) => {
      if (!a.anomaly_time) return;
      const t = new Date(a.anomaly_time);
      // JS getDay(): 0 = Sunday → convert to Monday-first index.
      add((t.getDay() + 6) % 7, t.getHours(), 2);
    });

    const max = Math.max(1, ...grid.flat());
    return { grid, max, hasData };
  }, [routes, patterns, digital, anomalies]);

  const dayOfWeekData = useMemo(
    () =>
      DAY_LABELS.map((label, i) => ({
        label,
        weight: Math.round(rhythm.grid[i].reduce((a, b) => a + b, 0) * 10) / 10,
      })),
    [rhythm]
  );

  const topLocations = useMemo(() => {
    const byName = new Map<string, { name: string; visits: number; hours: number }>();
    dwells.forEach((d) => {
      const name = d.location_name || 'Unknown location';
      const cur = byName.get(name) || { name, visits: 0, hours: 0 };
      cur.visits += d.visit_count || 0;
      cur.hours += (d.total_duration_seconds || 0) / 3600;
      byName.set(name, cur);
    });
    return Array.from(byName.values())
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 10)
      .map((l) => ({ ...l, hours: Math.round(l.hours * 10) / 10 }));
  }, [dwells]);

  // ----- Entity deviation ranking -------------------------------------------

  const rankedEntities = useMemo(
    () => [...report.entities].sort((a, b) => (b.anomaly_score || 0) - (a.anomaly_score || 0)),
    [report.entities]
  );

  // ----- AI assessment + recommendations ------------------------------------

  const aiEntities = useMemo(
    () => report.entities.filter((e) => e.ai_summary && e.ai_summary.trim().length > 0),
    [report.entities]
  );

  const recommendations = useMemo(() => {
    const recs: string[] = [];
    if (anomalyStats.unacknowledged > 0) {
      recs.push(
        `Review and acknowledge ${anomalyStats.unacknowledged} unacknowledged high-severity anomal${anomalyStats.unacknowledged === 1 ? 'y' : 'ies'} — start with the Significant Events list above.`
      );
    }
    const strongPairs = correlations.filter((c) => (c.correlation_strength || 0) >= 0.5);
    if (strongPairs.length > 0) {
      recs.push(
        `Run link analysis on ${strongPairs.length} strongly correlated entity pair${strongPairs.length === 1 ? '' : 's'} to map the wider network (strongest: ${strongPairs[0].entity_a_id} ↔ ${strongPairs[0].entity_b_id}).`
      );
    }
    const immature = report.entities.filter((e) => e.baseline_established === false);
    if (immature.length > 0) {
      recs.push(
        `${immature.length} entit${immature.length === 1 ? 'y has' : 'ies have'} an immature behavioral baseline — extend the collection window before treating deviation scores as reliable.`
      );
    }
    const breaking = patterns.filter((p) => String(p.status || '').toLowerCase() === 'breaking');
    if (breaking.length > 0) {
      recs.push(
        `Task collection against ${breaking.length} breaking routine${breaking.length === 1 ? '' : 's'} — a departure from established pattern often precedes a significant event.`
      );
    }
    const shared = routes.filter((r) => (r.shared_with_entities || []).length > 0);
    if (shared.length > 0) {
      recs.push(
        `Examine ${shared.length} shared route${shared.length === 1 ? '' : 's'} for coordinated movement; overlay trip instances to confirm co-travel.`
      );
    }
    if (recs.length === 0) {
      recs.push(
        'No urgent follow-on actions indicated. Consider widening the date range or adding analysis types for a broader behavioral picture.'
      );
    }
    return recs;
  }, [anomalyStats, correlations, report.entities, patterns, routes]);

  // ----- Render --------------------------------------------------------------

  const heatCellColor = (v: number) => {
    if (v <= 0) return alpha(theme.palette.text.primary, 0.05);
    const intensity = Math.min(1, v / rhythm.max);
    return alpha(theme.palette.primary.main, 0.15 + intensity * 0.8);
  };

  const severityDot = (severity: string) =>
    severity === 'critical' || severity === 'high'
      ? theme.palette.error.main
      : severity === 'medium'
        ? theme.palette.warning.main
        : theme.palette.info.main;

  return (
    <Stack spacing={4} sx={{ pb: 4 }}>
      {/* ================= Executive Summary ================= */}
      <Box>
        <SectionHeader
          icon={FindingIcon}
          overline="Executive Summary"
          title="Key Findings"
          caption={`${windowDays}-day reporting window · ${report.entities.length} entities under analysis`}
        />

        {/* KPI strip */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Entities" value={report.entities.length} sub={`${confidence.baselines} with mature baseline`} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              label="Anomalies"
              value={anomalyStats.total}
              sub={anomalyStats.high > 0 ? `${anomalyStats.high} high severity` : 'none high severity'}
              accent={anomalyStats.high > 0 ? theme.palette.error.main : theme.palette.success.main}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard label="Locations" value={uniqueLocations} sub="distinct places visited" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              label="Routes"
              value={report.section_counts?.repeated_routes ?? routes.length}
              sub="recurring movement corridors"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              label="Patterns"
              value={report.section_counts?.patterns ?? patterns.length}
              sub={`${patterns.filter((p) => String(p.status || '').toLowerCase() === 'breaking').length} breaking`}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard
              label="Data Confidence"
              value={confidence.avg != null ? `${Math.round(confidence.avg * 100)}%` : '—'}
              sub={`avg ${confidence.avgDays} days of data`}
              accent={
                confidence.avg == null
                  ? undefined
                  : confidence.avg >= 0.7
                    ? theme.palette.success.main
                    : theme.palette.warning.main
              }
            />
          </Grid>
        </Grid>

        {/* Findings list */}
        <Stack spacing={1}>
          {keyFindings.map((f, i) => {
            const t = tones[f.tone];
            return (
              <Card
                key={i}
                variant="outlined"
                sx={{ borderLeft: '4px solid', borderLeftColor: t.color, bgcolor: t.bg }}
              >
                <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Typography
                      variant="subtitle2"
                      sx={{ color: t.color, fontWeight: 800, minWidth: 20, mt: 0.1 }}
                    >
                      {i + 1}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {f.title}
                        </Typography>
                        <Chip
                          label={t.label}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            color: t.color,
                            bgcolor: alpha(t.color, 0.15),
                          }}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {f.detail}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Box>

      {/* ================= Behavioral Insights ================= */}
      <Box>
        <SectionHeader
          icon={EntityIcon}
          overline="Behavioral Insights"
          title="Who, Where, and What Changed"
        />
        <Grid container spacing={2}>
          {/* Deviations from baseline */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <TrendingUpIcon sx={{ fontSize: 18, color: 'error.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Deviations from Baseline
                  </Typography>
                </Stack>
                {rankedEntities.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No entity summaries available for this window.
                  </Typography>
                )}
                <Stack spacing={1}>
                  {rankedEntities.slice(0, 6).map((e) => (
                    <Box key={e.entity_id}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {e.entity_id}
                          </Typography>
                          {e.baseline_established === false && (
                            <MuiTooltip title="Baseline immature — deviation scores are provisional">
                              <Chip label="new" size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                            </MuiTooltip>
                          )}
                          {(e.anomaly_score || 0) >= 0.5 ? (
                            <TrendingUpIcon sx={{ fontSize: 14, color: 'error.main' }} />
                          ) : (e.anomaly_score || 0) >= 0.25 ? (
                            <TrendingFlatIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                          ) : (
                            <TrendingDownIcon sx={{ fontSize: 14, color: 'success.main' }} />
                          )}
                        </Stack>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {Math.round((e.anomaly_score || 0) * 100)}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, (e.anomaly_score || 0) * 100)}
                        color={
                          (e.anomaly_score || 0) >= 0.5
                            ? 'error'
                            : (e.anomaly_score || 0) >= 0.25
                              ? 'warning'
                              : 'success'
                        }
                        sx={{ height: 5, borderRadius: 3, mt: 0.25 }}
                      />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Significant events */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <AnomalyIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Most Significant Events
                  </Typography>
                </Stack>
                {notableEvents.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No anomalies detected in this window.
                  </Typography>
                )}
                <Stack spacing={1} divider={<Divider flexItem />}>
                  {notableEvents.slice(0, 4).map((a: any) => (
                    <Box key={a.id}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: severityDot(a.severity),
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {ANOMALY_TYPE_LABELS[a.anomaly_type] || a.anomaly_type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {a.entity_id} · {formatDateTime(a.anomaly_time)}
                        </Typography>
                      </Stack>
                      {(a.expected_behavior || a.actual_behavior) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2 }}>
                          {a.expected_behavior || '—'}
                          {'  '}
                          <ArrowIcon sx={{ fontSize: 10, verticalAlign: 'middle', mx: 0.25 }} />
                          {'  '}
                          {a.actual_behavior || '—'}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Frequent locations */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <PlaceIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Frequent Locations
                  </Typography>
                </Stack>
                {topLocations.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Dwell analysis was not included or returned no locations.
                  </Typography>
                )}
                <Stack spacing={0.75}>
                  {topLocations.slice(0, 6).map((l, i) => (
                    <Stack key={l.name} direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" sx={{ minWidth: 16, color: 'text.secondary', fontWeight: 700 }}>
                        {i + 1}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                          {l.name}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={topLocations[0].visits > 0 ? (l.visits / topLocations[0].visits) * 100 : 0}
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {l.visits} visits · {l.hours}h
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Routine patterns */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <PatternIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Routine Patterns
                  </Typography>
                </Stack>
                {patterns.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Pattern analysis was not included or returned no recurring patterns.
                  </Typography>
                )}
                <Stack spacing={0.75}>
                  {patterns.slice(0, 5).map((p: any) => (
                    <Stack key={p.id} direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={String(p.status || 'stable').toLowerCase()}
                        size="small"
                        color={
                          String(p.status).toLowerCase() === 'breaking'
                            ? 'error'
                            : String(p.status).toLowerCase() === 'emerging'
                              ? 'info'
                              : 'default'
                        }
                        sx={{ height: 18, fontSize: '0.6rem', textTransform: 'capitalize', minWidth: 62 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                          {p.entity_id}: {(p.pattern_type || '').replace(/_/g, ' ')}
                          {p.location_name || p.route_name ? ` — ${p.location_name || p.route_name}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {p.occurrence_count}× ·{' '}
                          {p.adherence_rate != null ? `${Math.round(p.adherence_rate * 100)}% adherence` : ''}
                          {Array.isArray(p.days_of_week) && p.days_of_week.length > 0
                            ? ` · ${p.days_of_week.slice(0, 3).join(', ')}`
                            : ''}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* ================= Activity Timeline ================= */}
      {(timeline.length > 0 || notableEvents.length > 0) && (
        <Box>
          <SectionHeader
            icon={TrendingUpIcon}
            overline="Activity Timeline"
            title="How Behavior Evolved Across the Window"
            caption="Daily atypicality (area) against detector events (bars)"
          />
          {timeline.length > 0 && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ pb: 0.5 }}>
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={timeline} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="score"
                      domain={[0, 1]}
                      tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis yAxisId="count" orientation="right" hide />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(value: any, name: any) => {
                        if (name === 'Avg atypicality' || name === 'Peak atypicality') {
                          return [`${Math.round(Number(value) * 100)}%`, name];
                        }
                        return [value, name];
                      }}
                    />
                    <Area
                      yAxisId="score"
                      type="monotone"
                      dataKey="avgAtypicality"
                      name="Avg atypicality"
                      stroke={theme.palette.primary.main}
                      fill={alpha(theme.palette.primary.main, 0.25)}
                      strokeWidth={2}
                    />
                    <Area
                      yAxisId="score"
                      type="monotone"
                      dataKey="maxAtypicality"
                      name="Peak atypicality"
                      stroke={alpha(theme.palette.warning.main, 0.9)}
                      fill="transparent"
                      strokeDasharray="4 3"
                      strokeWidth={1.5}
                    />
                    <Bar yAxisId="count" dataKey="anomalyCount" name="Detector events" barSize={10}>
                      {timeline.map((d) => (
                        <Cell
                          key={d.day}
                          fill={
                            d.highSeverityCount > 0
                              ? alpha(theme.palette.error.main, 0.75)
                              : alpha(theme.palette.info.main, 0.5)
                          }
                        />
                      ))}
                    </Bar>
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Anomaly callouts */}
          {notableEvents.length > 0 && (
            <Stack spacing={0}>
              {notableEvents.map((a: any, i: number) => (
                <Stack key={a.id} direction="row" spacing={1.5}>
                  {/* Timeline gutter */}
                  <Stack alignItems="center" sx={{ width: 14, flexShrink: 0 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: severityDot(a.severity),
                        border: '2px solid',
                        borderColor: 'background.paper',
                        boxShadow: `0 0 0 2px ${alpha(severityDot(a.severity), 0.4)}`,
                        mt: 0.75,
                      }}
                    />
                    {i < notableEvents.length - 1 && (
                      <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', my: 0.5 }} />
                    )}
                  </Stack>
                  <Box sx={{ pb: 2, flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {ANOMALY_TYPE_LABELS[a.anomaly_type] || a.anomaly_type}
                      </Typography>
                      <Chip
                        label={a.severity}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          textTransform: 'capitalize',
                          color: severityDot(a.severity),
                          bgcolor: alpha(severityDot(a.severity), 0.12),
                          fontWeight: 700,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {a.entity_id} · {formatDateTime(a.anomaly_time)}
                        {a.deviation_score != null ? ` · deviation ${a.deviation_score.toFixed(2)}` : ''}
                      </Typography>
                    </Stack>
                    {(a.expected_behavior || a.actual_behavior) && (
                      <Typography variant="body2" color="text.secondary">
                        Expected {a.expected_behavior || '—'}; observed {a.actual_behavior || '—'}.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {/* ================= Geospatial Evidence (reduced map) ================= */}
      {(mapDwells.length > 0 || mapRoutes.length > 0) && (
        <Box>
          <Accordion
            variant="outlined"
            expanded={mapOpen}
            onChange={(_e, v) => setMapOpen(v)}
            disableGutters
            sx={{ '&:before': { display: 'none' }, borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <MapIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Geospatial Evidence
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supporting map — {mapDwells.length} dwell locations, {mapRoutes.length} recurring routes
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {mapOpen && (
                <EnhancedMapVisualizationGL
                  dwellLocations={mapDwells}
                  routes={mapRoutes}
                  activeTab={1}
                  mapHeight={420}
                  compactControls
                />
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* ================= Analytics ================= */}
      {(rhythm.hasData || topLocations.length > 0) && (
        <Box>
          <SectionHeader
            icon={AnalyticsIcon}
            overline="Analytics"
            title="Behavioral Rhythm and Frequency"
          />
          <Grid container spacing={2}>
            {rhythm.hasData && (
              <Grid item xs={12} lg={7}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                      Activity Rhythm
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Time-of-day × day-of-week intensity from routes, patterns, digital activity, and detector events
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Box sx={{ minWidth: 560 }}>
                        {/* Hour scale */}
                        <Stack direction="row" spacing={0.25} sx={{ ml: '34px', mb: 0.25 }}>
                          {Array.from({ length: 24 }, (_, h) => (
                            <Typography
                              key={h}
                              variant="caption"
                              sx={{
                                flex: 1,
                                textAlign: 'center',
                                fontSize: '0.55rem',
                                color: 'text.secondary',
                                visibility: h % 4 === 0 ? 'visible' : 'hidden',
                              }}
                            >
                              {h}
                            </Typography>
                          ))}
                        </Stack>
                        {DAY_LABELS.map((label, d) => (
                          <Stack key={label} direction="row" spacing={0.25} alignItems="center" sx={{ mb: 0.25 }}>
                            <Typography
                              variant="caption"
                              sx={{ width: 30, fontSize: '0.65rem', color: 'text.secondary', flexShrink: 0 }}
                            >
                              {label}
                            </Typography>
                            {rhythm.grid[d].map((v, h) => (
                              <MuiTooltip
                                key={h}
                                title={`${label} ${formatHour(h)} — ${v > 0 ? `intensity ${Math.round((v / rhythm.max) * 100)}%` : 'no observed activity'}`}
                                disableInteractive
                              >
                                <Box
                                  sx={{
                                    flex: 1,
                                    height: 16,
                                    borderRadius: 0.5,
                                    bgcolor: heatCellColor(v),
                                    cursor: 'default',
                                  }}
                                />
                              </MuiTooltip>
                            ))}
                          </Stack>
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12} sm={6} lg={rhythm.hasData ? 5 : 6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Day-of-Week Activity
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={dayOfWeekData} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                        formatter={(value: any) => [value, 'Activity weight']}
                      />
                      <Bar dataKey="weight" radius={[4, 4, 0, 0]}>
                        {dayOfWeekData.map((d, i) => {
                          const max = Math.max(...dayOfWeekData.map((x) => x.weight), 1);
                          return (
                            <Cell
                              key={i}
                              fill={alpha(theme.palette.primary.main, 0.35 + 0.6 * (d.weight / max))}
                            />
                          );
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {topLocations.length > 0 && (
              <Grid item xs={12} sm={6} lg={rhythm.hasData ? 12 : 6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Visit Frequency — Top Locations
                    </Typography>
                    <ResponsiveContainer width="100%" height={Math.max(160, topLocations.length * 30)}>
                      <BarChart
                        data={topLocations}
                        layout="vertical"
                        margin={{ top: 0, right: 24, bottom: 0, left: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} tickLine={false} axisLine={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={170}
                          tick={{ fontSize: 11, fill: theme.palette.text.primary }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                          formatter={(value: any, name: any) =>
                            name === 'visits' ? [value, 'Visits'] : [`${value}h`, 'Time on site']
                          }
                        />
                        <Bar dataKey="visits" name="visits" fill={alpha(theme.palette.primary.main, 0.8)} radius={[0, 4, 4, 0]} barSize={14} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* ================= AI Intelligence Assessment ================= */}
      <Box>
        <SectionHeader
          icon={AiIcon}
          overline="AI-Generated Intelligence Assessment"
          title="Narrative Summary and Recommended Actions"
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={aiEntities.length > 0 ? 7 : 12}>
            {aiEntities.length > 0 ? (
              <Stack spacing={1.5}>
                {aiEntities.map((e: PolEntity) => (
                  <Card
                    key={e.entity_id}
                    variant="outlined"
                    sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <AiIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {e.entity_id}
                        </Typography>
                        {e.data_confidence != null && (
                          <Chip
                            icon={<ConfidenceIcon sx={{ fontSize: 12 }} />}
                            label={`${Math.round(e.data_confidence * 100)}% confidence`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {e.ai_summary}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    No AI narrative summaries are available for this window. Summaries are
                    generated by the nightly Pattern of Life pipeline.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
          <Grid item xs={12} md={aiEntities.length > 0 ? 5 : 12}>
            <Card
              variant="outlined"
              sx={{ height: '100%', borderColor: alpha(theme.palette.primary.main, 0.4) }}
            >
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Recommended Follow-On Analysis
                </Typography>
                <Stack spacing={1}>
                  {recommendations.map((r, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                      <ArrowIcon sx={{ fontSize: 14, color: 'primary.main', mt: 0.35, flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary">
                        {r}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};

export default PolReportDashboard;
